import React, { FC, createContext, useState, useEffect } from "react";
import ReactResizeDetector from "react-resize-detector";
import {
  GlobalStyle,
  targetBaseFontSizePx,
  colors,
  spaceRem,
  fontStacks,
} from "../global-style";
import { Helmet } from "react-helmet";
import { NavbarWide } from "./navbar-wide";
import { NavbarNarrow } from "./navbar-narrow";
import { useStaticQuery, graphql } from "gatsby";
import { LayoutQuery } from "../../graphql-types";
import { FluidObject } from "gatsby-image";
import Img from "gatsby-image";
import { useLocation } from "@reach/router";
import BackgroundImage, { IFluidObject } from "gatsby-background-image";
import { css } from "styled-components";

type LayoutContext = {
  remInPx: number;
  vwInPx?: number;
};
export const LayoutContext = createContext<LayoutContext>({} as LayoutContext);

const Layout: FC<{ pageContext: { id?: string } }> = props => {
  const data = useStaticQuery<LayoutQuery>(graphql`
    query Layout {
      footer: file(relativePath: { eq: "footer.png" }) {
        childImageSharp {
          fluid(quality: 90, maxWidth: 1920) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `);

  const location = useLocation();
  const currentLanguage = location.pathname.split("/").filter(v => v)[0] as
    | "nl"
    | "en";

  return (
    <>
      <GlobalStyle />

      <Helmet
        htmlAttributes={{
          lang: currentLanguage,
        }}
        title="North Sea Energy"
      ></Helmet>

      <ReactResizeDetector
        refreshMode="debounce"
        refreshRate={250}
        handleWidth={true}
        render={({ width: vwInPx }) => {
          const remInPx =
            typeof window !== "undefined"
              ? parseFloat(
                  window.getComputedStyle(document.documentElement).fontSize
                )
              : targetBaseFontSizePx;

          return (
            <LayoutContext.Provider
              value={{
                remInPx,
                vwInPx,
              }}
            >
              <div
                css={`
                  @media (min-width: 1200px) {
                    display: none;
                  }
                `}
              >
                <NavbarNarrow />
              </div>

              <div
                css={`
                  display: none;

                  @media (min-width: 1200px) {
                    display: block;
                  }
                `}
              >
                <NavbarWide />
              </div>

              <main>{props.children}</main>

              <footer
                style={{
                  backgroundColor: colors.darkestBlue,
                  position: "relative",
                  paddingTop: `${2.5 * spaceRem}rem`,
                }}
              >
                <Img
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                  }}
                  fluid={data.footer?.childImageSharp?.fluid as FluidObject}
                />

                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    paddingLeft: `${0.5 * spaceRem}rem`,
                    paddingRight: `${0.5 * spaceRem}rem`,
                  }}
                >
                  <h3
                    style={{
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: colors.mediumBlue,
                      letterSpacing: 2,
                      marginBottom: `${0.25 * spaceRem}rem`,
                    }}
                  >
                    {(currentLanguage === "en" && "Newsletter") ||
                      (currentLanguage === "nl" && "Nieuwsbrief")}
                  </h3>

                  <h2
                    style={{
                      color: colors.white,
                      marginBottom: `${1 * spaceRem}rem`,
                    }}
                    css={(() => {
                      const minFontSizeRem = 1.5;
                      const maxFontSizeRem = 2.25;
                      const vwRangeStartPx = 400;

                      return css`
                        font-size: ${(minFontSizeRem *
                          (remInPx || targetBaseFontSizePx)) /
                          (vwRangeStartPx / 100)}vw;

                        @media (max-width: ${vwRangeStartPx}px) {
                          font-size: ${minFontSizeRem}rem;
                        }

                        @media (min-width: ${vwRangeStartPx *
                            (maxFontSizeRem / minFontSizeRem)}px) {
                          font-size: ${maxFontSizeRem}rem;
                        }
                      `;
                    })()}
                  >
                    {(currentLanguage === "en" && "Stay up to date") ||
                      (currentLanguage === "nl" && "Updates in je inbox")}
                  </h2>

                  <form
                    style={{
                      display: "flex",
                      width: "100%",
                      marginBottom: `${2 * spaceRem}rem`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                        border: `1px solid ${colors.darkBlue}`,
                      }}
                    >
                      <svg
                        width={14.737}
                        height={14.737}
                        viewBox="0 0 14.737 14.737"
                        style={{
                          marginLeft: `${0.5 * spaceRem}rem`,
                          flexShrink: 0,
                        }}
                      >
                        <path
                          d="M9.982 1.663C9.392 1.194 8.4.2 7.368.2s-2 .974-2.613 1.463A498.28 498.28 0 00.693 4.94 1.344 1.344 0 00.2 5.98v7.213a1.344 1.344 0 001.344 1.344h11.649a1.344 1.344 0 001.344-1.344V5.98a1.344 1.344 0 00-.493-1.04 498.28 498.28 0 00-4.062-3.277zm3.211 11.977H1.544a.448.448 0 01-.444-.447v-7.22a.448.448 0 01.165-.347c.736-.6 3.41-2.751 4.052-3.261C5.806 1.972 6.705 1.1 7.368 1.1s1.562.876 2.056 1.269c.642.51 3.316 2.66 4.052 3.261a.448.448 0 01.165.347v7.219a.448.448 0 01-.448.448zm-.245-6.057a.336.336 0 01-.05.473c-.639.516-1.544 1.239-2.917 2.328-.589.47-1.592 1.471-2.613 1.463s-2.039-1.005-2.613-1.463a338.17 338.17 0 01-2.916-2.327.336.336 0 01-.05-.473l.141-.174a.336.336 0 01.47-.05c.637.515 1.54 1.236 2.91 2.324.472.376 1.406 1.279 2.056 1.269.65.01 1.584-.893 2.056-1.269a301.022 301.022 0 002.913-2.324.336.336 0 01.472.05z"
                          fill={colors.mediumBlue}
                          stroke={colors.mediumBlue}
                          strokeWidth={0.4}
                        />
                      </svg>

                      <input
                        type="text"
                        name="email"
                        style={{
                          marginLeft: `${0.5 * spaceRem}rem`,
                          alignSelf: "stretch",
                          flexGrow: 1,
                          flexBasis: "30rem",
                          backgroundColor: "transparent",
                          border: "none",
                          outline: "none",
                          color: colors.white,
                          fontSize: "1rem",
                        }}
                        placeholder={
                          (currentLanguage === "en" && "Your e-mail address") ||
                          (currentLanguage === "nl" && "Je e-mailadres") ||
                          ""
                        }
                      />
                    </div>
                    <button>
                      {(currentLanguage === "en" && "Sign up") ||
                        (currentLanguage === "nl" && "Aanmelden")}
                    </button>
                  </form>

                  <ul
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      marginBottom: `${spaceRem}rem`,
                      color: colors.darkBlue,
                      fontSize: "0.65rem",
                      textTransform: "uppercase",
                      fontFamily: fontStacks.poppins,
                      fontWeight: 600,
                      letterSpacing: 2,
                    }}
                    css={`
                      li {
                        padding: ${0.5 * spaceRem}rem;
                      }
                    `}
                  >
                    <li>Voorwaarden</li>
                    <li>Cookies</li>
                    <li>
                      <a href="https://www.webba.nl/" target="_blank">
                        Website: Webba
                      </a>
                    </li>
                  </ul>
                </div>
              </footer>
            </LayoutContext.Provider>
          );
        }}
      />
    </>
  );
};

export default Layout;
